import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AnnouncementBar, Header } from "./components/layout/Header";
import { MobileStickyBar } from "./components/layout/MobileMenu";
import { Footer } from "./components/layout/Footer";
import HomePage from "./pages/HomePage";
import routes from "./content/generated/routes.json";

const Toaster = lazy(() => import("./components/ui/sonner").then((m) => ({ default: m.Toaster })));

const slug = (p) => p.replace(/^\/+|\/+$/g, "").replace(/\//g, "_");
const named = (loader, name) => lazy(() => loader().then((m) => ({ default: m[name] })));

// Each route downloads only its own template chunk and content file, in parallel.
const servicePage = (path) => lazy(() =>
  Promise.all([import("./pages/ServicePage"), import(`./content/generated/pages/${slug(path)}.json`)])
    .then(([m, c]) => ({ default: () => <m.default path={path} content={c.default} /> })));
const blogPost = (path, canonical) => lazy(() =>
  Promise.all([import("./pages/BlogPages"), import(`./content/generated/posts/${slug(path)}.json`)])
    .then(([m, c]) => ({ default: () => <m.default path={path} canonical={canonical} body={c.default} /> })));

const infoPages = () => import("./pages/InfoPages");
const adminPages = () => import("./pages/AdminPages");
const BlogIndexPage = named(() => import("./pages/BlogPages"), "BlogIndexPage");
const AboutPage = named(infoPages, "AboutPage");
const FaqPage = named(infoPages, "FaqPage");
const ContactPage = named(infoPages, "ContactPage");
const SchedulePage = named(infoPages, "SchedulePage");
const ResourcesPage = named(infoPages, "ResourcesPage");
const NotFoundPage = named(infoPages, "NotFoundPage");
const AdminLeadsPage = named(adminPages, "AdminLeadsPage");
const MigrationAuditPage = named(adminPages, "MigrationAuditPage");

const TEMPLATED = routes.pages.map((p) => [p, servicePage(p)]);
const POSTS = routes.blogs.flatMap(([path, alias]) => {
  const out = [[path, blogPost(path)]];
  if (alias) out.push([alias, blogPost(path, path)]);
  return out;
});

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView(); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const Layout = () => (
  <>
    <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded focus:bg-gold focus:px-4 focus:py-2 focus:text-plum">Skip to content</a>
    <AnnouncementBar />
    <Header />
    <main id="main">
      <Suspense fallback={<div className="min-h-screen bg-plum-deep" aria-busy="true" />}><Outlet /></Suspense>
    </main>
    <Footer />
    <MobileStickyBar />
  </>
);

// Sonner injects ~15KB of CSS into <head> when mounted, so only mount it once a visitor starts using a form.
const ToasterOnDemand = () => {
  const { pathname } = useLocation();
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (on) return undefined;
    if (pathname.startsWith("/admin")) { setOn(true); return undefined; }
    const wake = (e) => { if (e.target?.closest?.("form")) setOn(true); };
    document.addEventListener("focusin", wake);
    document.addEventListener("pointerdown", wake);
    return () => { document.removeEventListener("focusin", wake); document.removeEventListener("pointerdown", wake); };
  }, [on, pathname]);
  return on ? <Suspense fallback={null}><Toaster position="top-center" richColors /></Suspense> : null;
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollManager />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            {TEMPLATED.map(([p, C]) => <Route key={p} path={p} element={<C />} />)}
            {POSTS.map(([p, C]) => <Route key={p} path={p} element={<C />} />)}
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/drain-talk-blog" element={<BlogIndexPage path="/drain-talk-blog" />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faqs" element={<FaqPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/schedule-service" element={<SchedulePage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/admin/leads" element={<AdminLeadsPage />} />
            <Route path="/migration-audit" element={<MigrationAuditPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        <ToasterOnDemand />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
